import React, { useMemo, useState } from 'react';
import { Plus, X, Check, Trash2, Edit2, ChevronRight, CalendarDays, List } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatApiError } from '../utils/apiErrors';
import { isStaffActiveOnDate, buildStaffDropdownOptions, getStaffAttributedName } from '../utils/payroll';
import { formatUiDate as formatDate } from '../utils/uiFormat';
import UiDateField from '../components/ui/UiDateField';
import UiTimeField from '../components/ui/UiTimeField';
import UiPanel from '../components/ui/UiPanel';
import UiSearchField from '../components/ui/UiSearchField';
import SortableHeader from '../components/SortableHeader';
import useSortable from '../hooks/useSortable';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBREVS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MO_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const isoDate = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');
const getDayOfWeek = (dateStr) => { const d = new Date(`${dateStr}T12:00:00`); return (d.getDay() + 6) % 7; };
const getStaffFullName = (m) => m?.full_name || `${m?.first_name || ''} ${m?.last_name || ''}`.trim();
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

const getISOWeekStr = (d = new Date()) => {
  const date = new Date(d);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const w1 = new Date(date.getFullYear(), 0, 4);
  const wk = 1 + Math.round(((date - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7);
  return `${date.getFullYear()}-W${String(wk).padStart(2, '0')}`;
};
const weekToMonday = (weekStr) => {
  if (!weekStr) return null;
  const [y, w] = weekStr.split('-W').map(Number);
  const jan4 = new Date(y, 0, 4);
  const adj = (jan4.getDay() + 6) % 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - adj + (w - 1) * 7);
  return mon;
};

const initialRoster = { staff: '', roster_date: '', shift_start: '09:00', shift_end: '18:00', notes: '' };

/**
 * RosterTab — self-contained roster manager (Option A: page owns data, this owns roster UI).
 * Roster rows are stored only for staff whose shift differs from shop hours; every other
 * date falls back to shop hours automatically (server + payroll already do this).
 */
export default function RosterTab({
  rosters = [],
  staff = [],
  shopHours = [],
  leaveEntries = [],
  publicHolidays = [],
  currentBusinessDate,
  canManage = false,
  onDataChanged,
}) {
  const [view, setView] = useState('calendar');
  const [search, setSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [calMonthYM, setCalMonthYM] = useState(() => (currentBusinessDate || isoDate(new Date())).slice(0, 7));
  const [dayFilter, setDayFilter] = useState('');
  const { sortKey, sortDir, handleSort, sortData } = useSortable('roster_date', 'desc');

  // Single add/edit
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [rosterForm, setRosterForm] = useState(initialRoster);
  const [saving, setSaving] = useState(false);

  // Weekly bulk builder
  const [showBuilder, setShowBuilder] = useState(false);
  const [inline, setInline] = useState({ week: getISOWeekStr(), days: [0, 1, 2, 3, 4], shift_start: '09:00', shift_end: '18:00', staff: '', allStaff: false });
  const [pending, setPending] = useState([]);
  const [savingAll, setSavingAll] = useState(false);

  // ── Derived helpers ──────────────────────────────────────────────
  const holidayMap = useMemo(() => {
    const m = {};
    publicHolidays.forEach((h) => { if (h.holiday_date) m[h.holiday_date] = h; });
    return m;
  }, [publicHolidays]);

  const leaveMap = useMemo(() => {
    const m = {};
    leaveEntries.forEach((l) => {
      if (!l.start_date) return;
      const start = new Date(l.start_date); const end = new Date(l.end_date || l.start_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        m[`${l.staff}_${d.toISOString().split('T')[0]}`] = l;
      }
    });
    return m;
  }, [leaveEntries]);

  const getEffectiveShopHours = (targetDate, dow) =>
    [...shopHours]
      .filter((h) => h.day_of_week === dow && h.effective_from <= targetDate)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || null;

  const getShopHoursLabel = (targetDate) => {
    if (!targetDate) return '';
    const sh = getEffectiveShopHours(targetDate, getDayOfWeek(targetDate));
    if (sh && sh.open_time) return `Shop hours ${hhmm(sh.open_time)}${sh.close_time ? '–' + hhmm(sh.close_time) : ''}`;
    return 'Shop closed';
  };

  const buildStaffOptions = (targetDate, selectedValue = '') =>
    buildStaffDropdownOptions(staff, { onDate: targetDate, keepValue: selectedValue });

  const rosterFormStaffOptions = useMemo(
    () => buildStaffOptions(rosterForm.roster_date || currentBusinessDate, rosterForm.staff),
    [staff, rosterForm.roster_date, rosterForm.staff, currentBusinessDate]
  );

  const builderStaffOptions = useMemo(() => {
    const monday = weekToMonday(inline.week);
    const selectedDates = inline.days.length && monday
      ? inline.days.map((di) => { const d = new Date(monday); d.setDate(monday.getDate() + di); return isoDate(d); })
      : [];
    const from = selectedDates.length ? selectedDates.slice().sort()[0] : currentBusinessDate;
    const to = selectedDates.length ? selectedDates.slice().sort().slice(-1)[0] : currentBusinessDate;
    return buildStaffDropdownOptions(staff, { fromDate: from, toDate: to, keepValue: inline.staff });
  }, [staff, inline.week, inline.days, inline.staff, currentBusinessDate]);

  // ── Calendar aggregation ─────────────────────────────────────────
  const calendarData = useMemo(() => {
    const [yr, mo] = calMonthYM.split('-').map(Number);
    const daysInMonth = new Date(yr, mo, 0).getDate();
    const out = {};
    for (let day = 1; day <= daysInMonth; day++) {
      const ds = `${yr}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const eligible = staff.filter((s) => isStaffActiveOnDate(s, ds) && (!staffFilter || String(s.id) === String(staffFilter)));
      const dayRosters = rosters.filter((r) => r.roster_date === ds && (!staffFilter || String(r.staff) === String(staffFilter)));
      const withRoster = new Set(dayRosters.map((r) => String(r.staff)));
      out[ds] = { total: eligible.length, rosterCount: eligible.filter((s) => withRoster.has(String(s.id))).length };
    }
    return out;
  }, [rosters, staff, staffFilter, calMonthYM]);

  // ── Table rows ───────────────────────────────────────────────────
  const tableRows = useMemo(() => {
    let rows = rosters;
    if (staffFilter) rows = rows.filter((r) => String(r.staff) === String(staffFilter));
    if (dayFilter) rows = rows.filter((r) => r.roster_date === dayFilter);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => String(r.staff_name || '').toLowerCase().includes(q) || String(r.notes || '').toLowerCase().includes(q));
    }
    return sortData(rows);
  }, [rosters, staffFilter, dayFilter, search, sortData]);

  // ── Mutations ────────────────────────────────────────────────────
  const openAdd = () => {
    const sh = getEffectiveShopHours(currentBusinessDate, getDayOfWeek(currentBusinessDate));
    setInline((r) => ({ ...r, week: getISOWeekStr(), shift_start: sh ? hhmm(sh.open_time) : '09:00', shift_end: sh ? hhmm(sh.close_time) : '18:00' }));
    setPending([]);
    setShowBuilder(true);
  };

  const openEdit = (item) => {
    const sh = (!item.shift_start || !item.shift_end) ? getEffectiveShopHours(item.roster_date, getDayOfWeek(item.roster_date)) : null;
    setEditingItem(item);
    setRosterForm({
      ...initialRoster, ...item,
      shift_start: hhmm(item.shift_start) || (sh ? hhmm(sh.open_time) : ''),
      shift_end: hhmm(item.shift_end) || (sh ? hhmm(sh.close_time) : ''),
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingItem(null); setRosterForm(initialRoster); };

  const setFormDate = (value) => {
    const sh = getEffectiveShopHours(value, getDayOfWeek(value));
    setRosterForm((f) => ({ ...f, roster_date: value, shift_start: sh ? hhmm(sh.open_time) : f.shift_start, shift_end: sh ? hhmm(sh.close_time) : f.shift_end }));
  };

  const saveSingle = async (e) => {
    e.preventDefault();
    if (!rosterForm.staff) { toast.error('Select a staff member'); return; }
    if (!rosterForm.roster_date) { toast.error('Select a date'); return; }
    setSaving(true);
    try {
      const payload = { staff: rosterForm.staff, roster_date: rosterForm.roster_date, shift_start: rosterForm.shift_start || null, shift_end: rosterForm.shift_end || null, notes: rosterForm.notes || '' };
      if (editingItem?.id) await api.patch(`/staff-rosters/${editingItem.id}/`, payload);
      else await api.post('/staff-rosters/', payload);
      toast.success(editingItem ? 'Roster updated' : 'Roster added');
      closeModal();
      onDataChanged && onDataChanged();
    } catch (err) { toast.error(formatApiError(err, 'Failed to save roster')); }
    finally { setSaving(false); }
  };

  const deleteRoster = async (item) => {
    if (!window.confirm('Delete this roster entry?')) return;
    try {
      await api.delete(`/staff-rosters/${item.id}/`);
      toast.success('Roster deleted');
      onDataChanged && onDataChanged();
    } catch (err) { toast.error(formatApiError(err, 'Failed to delete roster')); }
  };

  const applyPending = () => {
    if (!inline.allStaff && !inline.staff) { toast.error('Select a staff member'); return; }
    if (inline.days.length === 0) { toast.error('Select at least one day'); return; }
    if (!inline.week) { toast.error('Select a week'); return; }
    const monday = weekToMonday(inline.week);
    const targets = inline.allStaff ? builderStaffOptions : builderStaffOptions.filter((o) => String(o.value) === String(inline.staff));
    const newEntries = targets.flatMap((s) =>
      inline.days.map((di) => {
        const d = new Date(monday); d.setDate(monday.getDate() + di);
        return { _key: `${Date.now()}_${s.value}_${di}`, staff: s.value, staff_name: s.label || '', roster_date: isoDate(d), day_abbr: DAY_ABBREVS[di], shift_start: inline.shift_start || null, shift_end: inline.shift_end || null };
      })
    );
    setPending((prev) => [...prev, ...newEntries]);
  };

  const saveAll = async () => {
    if (pending.length === 0) { toast.error('No entries to save'); return; }
    setSavingAll(true);
    try {
      await Promise.all(pending.map((e) => api.post('/staff-rosters/', { staff: e.staff, roster_date: e.roster_date, shift_start: e.shift_start, shift_end: e.shift_end })));
      toast.success(`${pending.length} roster entries saved`);
      setShowBuilder(false);
      setPending([]);
      onDataChanged && onDataChanged();
    } catch (err) { toast.error(formatApiError(err, 'Failed to save roster entries')); }
    finally { setSavingAll(false); }
  };

  const staffOptions = useMemo(
    () => [...staff].sort((a, b) => getStaffFullName(a).localeCompare(getStaffFullName(b))),
    [staff]
  );

  // Roster filter dropdown — staff in service during the viewed month, with time-bound attributes.
  const rosterFilterStaffOptions = useMemo(() => {
    const [yr, mo] = calMonthYM.split('-').map(Number);
    const from = `${yr}-${String(mo).padStart(2, '0')}-01`;
    const to = `${yr}-${String(mo).padStart(2, '0')}-${String(new Date(yr, mo, 0).getDate()).padStart(2, '0')}`;
    return buildStaffDropdownOptions(staff, { fromDate: from, toDate: to, keepValue: staffFilter });
  }, [staff, calMonthYM, staffFilter]);

  const changeMonth = (delta) => {
    const [y, m] = calMonthYM.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCalMonthYM(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const todayStr = isoDate(new Date());

  // ── Render ───────────────────────────────────────────────────────
  const renderCalendar = () => {
    const [yr, mo] = calMonthYM.split('-').map(Number);
    const daysInMonth = new Date(yr, mo, 0).getDate();
    const firstDow = new Date(yr, mo - 1, 1).getDay();
    const firstMon = firstDow === 0 ? 6 : firstDow - 1;
    const cells = [];
    for (let i = 0; i < firstMon; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return (
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition"><ChevronRight size={18} className="rotate-180" /></button>
          <h3 className="font-semibold text-gray-800 text-base">{MO_NAMES[mo - 1]} {yr}</h3>
          <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg transition"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} />;
            const ds = `${yr}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const data = calendarData[ds] || {};
            const holiday = holidayMap[ds];
            const isToday = ds === todayStr;
            const isFuture = ds > todayStr;
            const isPast = ds < todayStr;
            let bg = isPast ? 'bg-gray-300 border-gray-400' : 'bg-gray-50 border-gray-100';
            let txt = isPast ? 'text-gray-600' : 'text-gray-500';
            if (!isFuture) {
              if (data.rosterCount > 0 && data.rosterCount === data.total) { bg = 'bg-green-50 border-green-300'; txt = 'text-green-800'; }
              else if (data.rosterCount > 0) { bg = 'bg-yellow-50 border-yellow-300'; txt = 'text-yellow-800'; }
            }
            if (holiday && !data.rosterCount) { bg = 'bg-amber-50 border-amber-300'; txt = 'text-amber-800'; }
            return (
              <div key={ds} title={`Click to view ${ds}`}
                className={`${bg} border rounded-lg p-1.5 min-h-[58px] cursor-pointer hover:opacity-75 transition ${holiday ? 'ring-1 ring-amber-300' : ''} ${isToday ? 'ring-2 ring-primary-500' : ''}`}
                onClick={() => { setDayFilter(ds); setView('table'); }}>
                <div className={`text-xs font-bold ${txt}`}>{day}</div>
                {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                {!isFuture && data.total > 0 && (
                  <div className="mt-0.5">
                    {data.rosterCount > 0 && <div className="text-xs text-green-700 leading-tight">✓ {data.rosterCount}</div>}
                    {(data.total - data.rosterCount) > 0 && <div className="text-xs text-gray-400 leading-tight">— {data.total - data.rosterCount}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 mt-3">✓ rostered · — on shop-hours default. Staff without a roster row use shop hours automatically.</p>
      </div>
    );
  };

  const renderTable = () => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {dayFilter && (
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b text-sm text-gray-600">
          Showing {formatDate(dayFilter)}
          <button onClick={() => setDayFilter('')} className="text-primary-600 hover:underline text-xs">Clear</button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b text-gray-500">
            <tr>
              <SortableHeader label="Staff" col="staff_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Date" col="roster_date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Shift" col="shift_start" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className="px-4 py-3 text-left">Notes</th>
              <th className="px-4 py-3 text-left">Created By</th>
              {canManage && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tableRows.length === 0 && (
              <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-8 text-center text-gray-400">No roster entries.</td></tr>
            )}
            {tableRows.map((item) => {
              return (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{item.staff_name}</td>
                  <td className="px-4 py-3">{formatDate(item.roster_date)}</td>
                  <td className="px-4 py-3">{`${hhmm(item.shift_start) || '-'} – ${hhmm(item.shift_end) || '-'}`}</td>
                  <td className="px-4 py-3">{item.notes || '-'}</td>
                  <td className="px-4 py-3">{item.created_by_name || '-'}</td>
                  {canManage && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-primary-600 rounded"><Edit2 size={14} /></button>
                      <button onClick={() => deleteRoster(item)} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <button onClick={() => setView('calendar')} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === 'calendar' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600'}`}>Calendar</button>
          <button onClick={() => setView('table')} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === 'table' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600'}`}>Table</button>
        </div>
        <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All staff</option>
          {rosterFilterStaffOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canManage && (
          <button onClick={openAdd} className="ml-auto px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">Add Roster</button>
        )}
      </div>

      {view === 'table' && (
        <UiPanel bodyClassName="p-4">
          <UiSearchField value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search roster..." />
        </UiPanel>
      )}

      {view === 'calendar' ? renderCalendar() : renderTable()}

      {/* Single add/edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">{editingItem ? 'Edit' : 'Add'} Roster</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={saveSingle} className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
                  <select value={rosterForm.staff} onChange={(e) => setRosterForm({ ...rosterForm, staff: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
                    <option value="">Select staff</option>
                    {rosterFormStaffOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Roster Date *</label>
                  <UiDateField value={rosterForm.roster_date} onChange={setFormDate} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift Start</label>
                  <UiTimeField value={rosterForm.shift_start} onChange={(v) => setRosterForm({ ...rosterForm, shift_start: v })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift End</label>
                  <UiTimeField value={rosterForm.shift_end} onChange={(v) => setRosterForm({ ...rosterForm, shift_end: v })} />
                </div>
                <div className="md:col-span-2 -mt-2">
                  <p className="text-xs text-gray-400">Defaults to {getShopHoursLabel(rosterForm.roster_date)} for this date — edit if the staff shift differs, then save.</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={rosterForm.notes} onChange={(e) => setRosterForm({ ...rosterForm, notes: e.target.value })} placeholder="Notes" className="w-full px-3 py-2 border rounded-lg" rows="3" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={closeModal} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Weekly bulk builder */}
      {showBuilder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">Add Roster</h2>
              <button onClick={() => setShowBuilder(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Week</label>
                    <input type="week" value={inline.week} onChange={(e) => { const wk = e.target.value; const mon = weekToMonday(wk); const sh = mon ? getEffectiveShopHours(isoDate(mon), 0) : null; setInline((r) => ({ ...r, week: wk, shift_start: sh ? hhmm(sh.open_time) : r.shift_start, shift_end: sh ? hhmm(sh.close_time) : r.shift_end })); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                    {(() => { const mon = weekToMonday(inline.week); return mon ? <p className="text-[11px] text-gray-400 mt-1">{getShopHoursLabel(isoDate(mon))}</p> : null; })()}
                  </div>
                  <div><label className="block text-xs text-gray-500 mb-1">Shift Start</label><UiTimeField value={inline.shift_start} onChange={(v) => setInline((r) => ({ ...r, shift_start: v }))} className="w-[9rem]" /></div>
                  <div><label className="block text-xs text-gray-500 mb-1">Shift End</label><UiTimeField value={inline.shift_end} onChange={(v) => setInline((r) => ({ ...r, shift_end: v }))} className="w-[9rem]" /></div>
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-500">Staff *</label>
                      <label className="flex items-center gap-1 text-xs text-primary-600 cursor-pointer select-none">
                        <input type="checkbox" checked={inline.allStaff} onChange={(e) => setInline((r) => ({ ...r, allStaff: e.target.checked, staff: '' }))} className="w-3.5 h-3.5" /> All Staff
                      </label>
                    </div>
                    {inline.allStaff
                      ? <div className="w-full px-3 py-2 border border-primary-300 bg-primary-50 rounded-lg text-sm text-primary-700 font-medium">{builderStaffOptions.length} staff selected</div>
                      : <select value={inline.staff} onChange={(e) => setInline((r) => ({ ...r, staff: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"><option value="">Select Staff</option>{builderStaffOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {DAY_NAMES.map((name, i) => { const checked = inline.days.includes(i); return (<button key={i} type="button" onClick={() => setInline((r) => ({ ...r, days: checked ? r.days.filter((d) => d !== i) : [...r.days, i] }))} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${checked ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'}`}>{DAY_ABBREVS[i]}</button>); })}
                  <button type="button" onClick={() => setInline((r) => ({ ...r, days: r.days.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6] }))} className="px-3 py-1.5 text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg">{inline.days.length === 7 ? 'Deselect All' : 'Select All'}</button>
                  <button type="button" onClick={applyPending} className="ml-auto px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">Apply</button>
                </div>
              </div>
              {pending.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending ({pending.length} entries)</p>
                  <div className="border rounded-lg divide-y overflow-hidden max-h-64 overflow-y-auto">
                    {pending.map((e, i) => (
                      <div key={e._key} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-semibold">{e.day_abbr}</span>
                          <span className="text-gray-700 font-medium">{formatDate(e.roster_date)}</span>
                          <span className="text-gray-500">{e.staff_name}</span>
                          <span className="font-mono text-gray-600 text-xs">{hhmm(e.shift_start) || '?'} – {hhmm(e.shift_end) || '?'}</span>
                        </div>
                        <button onClick={() => setPending((p) => p.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button onClick={() => setShowBuilder(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">Cancel</button>
                <button onClick={saveAll} disabled={savingAll || pending.length === 0} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">{savingAll ? <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full inline-block" /> : null} Save All ({pending.length})</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
